import config from '../../config/env';
import React, { useState } from 'react';
import { useI18n, SUPPORTED_LANGUAGES, SupportedLanguage } from '../../contexts/I18nContext';
import './LanguageSelector.css';

interface LanguageSelectorProps {
  variant?: 'inline' | 'dropdown' | 'modal';
  showFlags?: boolean;
  showNativeNames?: boolean;
  compact?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  variant = 'dropdown',
  showFlags = true,
  showNativeNames = true,
  compact = false
}) => {
  const { language, setLanguage, t, availableLanguages } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  const handleLanguageChange = async (newLanguage: SupportedLanguage) => {
    if (newLanguage === language) {
      setIsOpen(false);
      return;
    }

    setIsChanging(true);

    try {
      // Small delay for visual feedback
      await new Promise(resolve => setTimeout(resolve, 300));

      setLanguage(newLanguage);
      setIsOpen(false);

      // Show success feedback
      if (config.dev.debug) {
        console.log('[LANGUAGE DEBUG] Changed to:', newLanguage);
      }

      // Optional: Show toast notification
      // You could integrate with a toast system here

    } catch (error) {
      console.error('[LANGUAGE] Error changing language:', error);
    } finally {
      setIsChanging(false);
    }
  };

  const currentLanguageInfo = SUPPORTED_LANGUAGES[language];

  if (variant === 'inline') {
    return (
      <div className="language-selector language-selector-inline">
        <div className="language-selector-label">
          {t('language.language')}
        </div>
        <div className="language-options-inline">
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              className={`language-option-inline ${lang.code === language ? 'active' : ''} ${isChanging ? 'changing' : ''}`}
              onClick={() => handleLanguageChange(lang.code)}
              disabled={isChanging}
            >
              {showFlags && <span className="language-flag">{lang.flag}</span>}
              <span className="language-name">
                {showNativeNames ? lang.nativeName : lang.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'dropdown') {
    return (
      <div className={`language-selector language-selector-dropdown ${compact ? 'compact' : ''}`}>
        <button
          className="language-selector-trigger"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isChanging}
          aria-label={t('language.selectLanguage')}
        >
          <div className="language-selector-current">
            {showFlags && (
              <span className="language-flag">{currentLanguageInfo.flag}</span>
            )}
            {!compact && (
              <span className="language-name">
                {showNativeNames ? currentLanguageInfo.nativeName : currentLanguageInfo.name}
              </span>
            )}
            <span className={`language-arrow ${isOpen ? 'open' : ''} ${isChanging ? 'changing' : ''}`}>
              {isChanging ? '⟳' : '▼'}
            </span>
          </div>
        </button>

        {isOpen && (
          <div className="language-dropdown animate-in">
            <div className="language-dropdown-header">
              <span className="language-dropdown-title">
                {t('language.selectLanguage')}
              </span>
            </div>

            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                className={`language-dropdown-option ${lang.code === language ? 'active' : ''}`}
                onClick={() => handleLanguageChange(lang.code)}
                disabled={isChanging}
              >
                <div className="language-option-content">
                  {showFlags && <span className="language-flag">{lang.flag}</span>}
                  <div className="language-names">
                    <span className="language-name-primary">{lang.nativeName}</span>
                    {lang.nativeName !== lang.name && (
                      <span className="language-name-secondary">{lang.name}</span>
                    )}
                  </div>
                  {lang.code === language && (
                    <span className="language-check">✓</span>
                  )}
                </div>
              </button>
            ))}

            <div className="language-dropdown-footer">
              <div className="language-current-status">
                {t('language.currentLanguage')}: {currentLanguageInfo.nativeName}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Modal variant (could be extended for more complex scenarios)
  return (
    <div className="language-selector language-selector-modal">
      <button
        className="language-selector-button"
        onClick={() => setIsOpen(true)}
        disabled={isChanging}
      >
        <span className="language-icon">🌐</span>
        <span className="language-text">{t('language.changeLanguage')}</span>
      </button>

      {isOpen && (
        <div className="language-modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="language-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="language-modal-header">
              <h3>{t('language.selectLanguage')}</h3>
              <button
                className="language-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label={t('common.close')}
              >
                ×
              </button>
            </div>

            <div className="language-modal-body">
              {availableLanguages.map((lang) => (
                <button
                  key={lang.code}
                  className={`language-modal-option ${lang.code === language ? 'active' : ''}`}
                  onClick={() => handleLanguageChange(lang.code)}
                  disabled={isChanging}
                >
                  <span className="language-flag large">{lang.flag}</span>
                  <div className="language-info">
                    <div className="language-name-primary">{lang.nativeName}</div>
                    <div className="language-name-secondary">{lang.name}</div>
                  </div>
                  {lang.code === language && (
                    <span className="language-check">✓</span>
                  )}
                </button>
              ))}
            </div>

            <div className="language-modal-footer">
              <button
                className="language-modal-cancel"
                onClick={() => setIsOpen(false)}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};